/**
 * Brand scanner — file-system layout, listing, creation, and variable editing
 * for brand `.md` files.
 *
 * Layout:
 *
 *   scope="project" → <workspaceRoot>/.appostle/brand/<name>.md
 *
 * The `.appostle/brand/` folder is repo-bound. Each `.md` file represents one section
 * of the brand (e.g. colors.md, typography.md, motion.md, voice.md).
 *
 * Brand files store an open-ended typed variable bag in YAML frontmatter.
 * Variables are the unit of extensibility — new fields are additive entries
 * in the `variables` array, no schema migration required.
 *
 * Frontmatter keys owned by this scanner:
 *   name, description, variables
 */

import type { Dirent } from "node:fs";
import fs from "node:fs/promises";
import path from "node:path";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type BrandScope = "project";

export type BrandVariableType =
  | "color"
  | "gradient"
  | "font"
  | "asset"
  | "text"
  | "number"
  | "select"
  | "record-list";
export type BrandSection = string;

export interface BrandVariable {
  key: string;
  type: BrandVariableType;
  label: string;
  value: string;
  section?: BrandSection;
  /** Only for type="select" */
  options?: string[];
  /** Absolute path on the host — populated for font/asset variables picked from disk. */
  fileRef?: string;
  /**
   * Human override flag — when true, AI-driven token regeneration must skip
   * this variable. Set on a per-token basis from the colors-page UI.
   */
  locked?: boolean;
  /**
   * Soft-disable flag. False means the variable is preserved on disk but
   * surfaced as muted/inactive in the UI. Absent or true means active.
   */
  enabled?: boolean;
}

export interface BrandEntry {
  /** Stable id — `${scope}:${name}`. */
  id: string;
  scope: BrandScope;
  /** Filename without `.md`. */
  name: string;
  /** Frontmatter description. Empty string if missing. */
  description: string;
  /** Typed variable bag. */
  variables: BrandVariable[];
  /** Absolute path to the .md file. */
  path: string;
  modifiedAt: string;
  size: number;
  /** Raw markdown body (frontmatter stripped). Empty string when no frontmatter present. */
  body: string;
}

export interface BrandFrontmatter {
  description?: string;
  variables?: BrandVariable[];
  /**
   * Optional dot-paths to remove from the underlying tokens.json before
   * applying variable patches. Only honored by the tokens-writer (JSON)
   * path; the .md scanner ignores this field.
   */
  removedKeys?: string[];
}

export interface ListBrandsArgs {
  workspaceRoot?: string;
}

export interface CreateBrandArgs {
  workspaceRoot?: string;
  name: string;
}

export interface CreateBrandResult {
  path: string;
}

export interface WriteBrandFrontmatterArgs {
  path: string;
  frontmatter: BrandFrontmatter;
}

/**
 * A derivation request — a "please also generate this companion asset from the
 * primary upload" instruction. Currently only `svg-mono` is supported: read
 * the just-saved SVG and write a flattened mono version with every fill /
 * stroke rewritten to `color`. Saved next to the primary in the same category
 * folder, named after `targetName`, sharing the primary's extension.
 *
 * Designed to be additive — future kinds (e.g. raster downscale, color theme)
 * slot in alongside without touching call sites.
 */
export type AssetDerivation = {
  kind: "svg-mono";
  /** Target color to flatten all fills / strokes to (e.g. "#000", "#fff"). */
  color: string;
  /** Bare filename stem for the derived asset. */
  targetName: string;
};

export interface AssetDerivationResult {
  targetName: string;
  /** Path relative to `.appostle/brand/` when derivation succeeded. */
  relativePath: string;
  /** Absolute path on disk when derivation succeeded. */
  absolutePath: string;
  /** Non-null when the derivation failed (e.g. source was raster, regex match failed). */
  error: string | null;
}

export interface CopyBrandAssetArgs {
  workspaceRoot: string;
  sourcePath: string;
  /** Bare filename without extension; extension is taken from sourcePath. */
  targetName: string;
  /**
   * Optional category folder under `assets/`, e.g. "logo" → assets/logo/<file>.
   * Lets each section bucket its assets ("logo", "typefaces", etc.) instead of
   * pooling everything flat. When omitted the file goes directly into
   * `assets/` (legacy flat layout).
   */
  category?: string;
  /** Optional derivations to run after the primary save. */
  derive?: AssetDerivation[];
}

export interface CopyBrandAssetResult {
  /** Path relative to `.appostle/brand/`, e.g. "assets/logo/logo-light-horizontal.svg". */
  relativePath: string;
  /** Absolute path of the copied file. */
  absolutePath: string;
  /** Per-derivation outcome (empty array when no derivations were requested). */
  derived: AssetDerivationResult[];
}

export interface UploadBrandAssetArgs {
  workspaceRoot: string;
  /** Bare filename without extension; extension is taken from sourceName when available. */
  targetName: string;
  sourceName?: string;
  /** Raw file bytes as base64. */
  dataBase64: string;
  /** Optional category folder under `assets/`. See {@link CopyBrandAssetArgs.category}. */
  category?: string;
  /** Optional derivations to run after the primary save. */
  derive?: AssetDerivation[];
}

const CATEGORY_REGEX = /^[a-z0-9][a-z0-9_-]*$/i;

function resolveAssetsDir(workspaceRoot: string, category: string | undefined): string {
  const base = path.join(workspaceRoot, ".appostle", "brand", "assets");
  if (!category) return base;
  if (!CATEGORY_REGEX.test(category) || category.includes("..")) {
    throw new Error(
      `Invalid asset category "${category}". Use letters, digits, dot, underscore, dash.`,
    );
  }
  return path.join(base, category);
}

function relativePathFor(category: string | undefined, fileName: string): string {
  return category ? `assets/${category}/${fileName}` : `assets/${fileName}`;
}

/**
 * Remove any files in `dir` whose name is `${targetName}.<otherExt>` while
 * preserving `keepFileName`. Lets a re-pick of the same brand slot replace
 * a previous file even when the new pick has a different extension (e.g.
 * RightFont .otf → Google .woff2 → folder .ttf), so the assets folder
 * never accumulates more than one file per slot. Best effort — silent on
 * read errors so a missing dir / permission glitch can't block the write.
 */
async function removeSiblingExtensions(
  dir: string,
  targetName: string,
  keepFileName: string,
): Promise<void> {
  let entries: string[];
  try {
    entries = await fs.readdir(dir);
  } catch {
    return;
  }
  for (const entry of entries) {
    if (entry === keepFileName) continue;
    if (!entry.startsWith(`${targetName}.`)) continue;
    // Guard against `${targetName}.something/` directories — only delete files.
    try {
      const stat = await fs.stat(path.join(dir, entry));
      if (!stat.isFile()) continue;
      await fs.unlink(path.join(dir, entry));
    } catch {
      /* best effort */
    }
  }
}

/**
 * Flatten every fill / stroke color in an SVG document to a single target.
 * Preserves `fill="none"` (intentionally transparent) and existing opacity.
 * Touches both the attribute form (`fill="#xxx"`) and the inline-style form
 * (`style="fill: #xxx"` / inside <style> blocks). The 95% case for brand SVGs;
 * gradients and embedded raster degrade to a still-valid SVG that just looks
 * slightly off rather than to a broken file.
 */
function convertSvgToMono(svg: string, color: string): string {
  return svg
    .replace(/(fill|stroke)="(?!none\s*"|inherit\s*"|currentcolor\s*")[^"]+"/gi, `$1="${color}"`)
    .replace(/(fill|stroke)\s*:\s*(?!none\b|inherit\b|currentcolor\b)[^;"}]+/gi, `$1:${color}`);
}

/**
 * Run the derivation set against a just-saved primary asset. Each derivation
 * produces an `AssetDerivationResult` regardless of success — the caller can
 * surface failures without aborting the whole upload, since the primary asset
 * itself is independently valid and useful.
 */
async function runDerivations(options: {
  primaryAbsolutePath: string;
  assetsDir: string;
  category: string | undefined;
  derive: AssetDerivation[];
}): Promise<AssetDerivationResult[]> {
  const { primaryAbsolutePath, assetsDir, category, derive } = options;
  if (derive.length === 0) return [];

  const ext = path.extname(primaryAbsolutePath).toLowerCase();
  const isSvg = ext === ".svg";

  const results: AssetDerivationResult[] = [];
  for (const spec of derive) {
    if (spec.kind !== "svg-mono") {
      results.push({
        targetName: spec.targetName,
        relativePath: "",
        absolutePath: "",
        error: `Unsupported derivation kind "${(spec as AssetDerivation).kind}"`,
      });
      continue;
    }

    if (!isSvg) {
      results.push({
        targetName: spec.targetName,
        relativePath: "",
        absolutePath: "",
        error: "svg-mono derivation requires an SVG source",
      });
      continue;
    }

    if (!TARGET_NAME_REGEX.test(spec.targetName) || spec.targetName.includes("..")) {
      results.push({
        targetName: spec.targetName,
        relativePath: "",
        absolutePath: "",
        error: `Invalid derivation targetName "${spec.targetName}"`,
      });
      continue;
    }

    try {
      const sourceText = await fs.readFile(primaryAbsolutePath, "utf8");
      const monoText = convertSvgToMono(sourceText, spec.color);
      const fileName = `${spec.targetName}${ext}`;
      const destAbs = path.resolve(assetsDir, fileName);
      const rel = path.relative(path.resolve(assetsDir), destAbs);
      if (rel.startsWith("..") || path.isAbsolute(rel)) {
        results.push({
          targetName: spec.targetName,
          relativePath: "",
          absolutePath: "",
          error: `Refusing to write derivation outside of assets dir`,
        });
        continue;
      }
      await fs.writeFile(destAbs, monoText, "utf8");
      results.push({
        targetName: spec.targetName,
        relativePath: relativePathFor(category, fileName),
        absolutePath: destAbs,
        error: null,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      results.push({
        targetName: spec.targetName,
        relativePath: "",
        absolutePath: "",
        error: message,
      });
    }
  }
  return results;
}

// ---------------------------------------------------------------------------
// Path resolution
// ---------------------------------------------------------------------------

const NAME_REGEX = /^[a-z0-9][a-z0-9._-]*$/i;

function resolveScopeDir(scope: BrandScope, workspaceRoot?: string): string {
  if (scope === "project") {
    if (!workspaceRoot) {
      throw new Error('workspaceRoot is required for scope "project"');
    }
    return path.join(workspaceRoot, ".appostle", "brand");
  }
  throw new Error(`Unknown scope: ${scope}`);
}

function allowedRoots(workspaceRoot?: string): string[] {
  const roots: string[] = [];
  if (workspaceRoot) {
    roots.push(path.join(workspaceRoot, ".appostle", "brand"));
  }
  return roots.map((r) => path.resolve(r));
}

function isInsideAllowedRoot(absPath: string, workspaceRoot?: string): boolean {
  const resolved = path.resolve(absPath);
  for (const root of allowedRoots(workspaceRoot)) {
    const rel = path.relative(root, resolved);
    if (rel === "" || (!rel.startsWith("..") && !path.isAbsolute(rel))) {
      return true;
    }
  }
  return false;
}

// ---------------------------------------------------------------------------
// Frontmatter parser — handles flat keys + nested variables array
// ---------------------------------------------------------------------------

const FRONTMATTER_RE = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?([\s\S]*)$/;

interface ParsedBrandFile {
  rawFrontmatterLines: string[];
  body: string;
  hadFrontmatter: boolean;
}

function parseBrandFile(text: string): ParsedBrandFile {
  const match = FRONTMATTER_RE.exec(text);
  if (!match) {
    return { rawFrontmatterLines: [], body: text, hadFrontmatter: false };
  }
  return {
    rawFrontmatterLines: (match[1] ?? "").split(/\r?\n/),
    body: match[2] ?? "",
    hadFrontmatter: true,
  };
}

function unquote(value: string): string {
  const trimmed = value.trim();
  if (trimmed.startsWith('"') && trimmed.endsWith('"')) {
    return trimmed.slice(1, -1).replace(/\\n/g, "\n").replace(/\\"/g, '"').replace(/\\\\/g, "\\");
  }
  if (trimmed.startsWith("'") && trimmed.endsWith("'")) {
    return trimmed.slice(1, -1);
  }
  return trimmed;
}

function quoteIfNeeded(value: string): string {
  if (/[:#\n\[\]]/.test(value) || value.startsWith(" ") || value.endsWith(" ")) {
    return `"${value.replace(/\\/g, "\\\\").replace(/"/g, '\\"').replace(/\n/g, "\\n")}"`;
  }
  return value;
}

interface ParsedFrontmatterData {
  description: string;
  variables: BrandVariable[];
}

/**
 * Parses the frontmatter of a brand file.
 * Handles: description (string) and variables (block array of objects).
 */
function parseFrontmatter(lines: string[]): ParsedFrontmatterData {
  const result: ParsedFrontmatterData = {
    description: "",
    variables: [],
  };

  let i = 0;
  while (i < lines.length) {
    const line = lines[i] ?? "";
    const keyMatch = /^([A-Za-z][A-Za-z0-9_-]*)\s*:\s*(.*)$/.exec(line);
    if (!keyMatch) {
      i++;
      continue;
    }
    const key = keyMatch[1];
    const rawValue = (keyMatch[2] ?? "").trim();

    if (key === "description") {
      result.description = unquote(rawValue);
      i++;
    } else if (key === "variables") {
      // Block array of objects — each item starts with "  - key: ..."
      const vars: BrandVariable[] = [];
      let j = i + 1;
      while (j < lines.length) {
        const next = lines[j] ?? "";
        // New list item
        if (/^\s+-\s+key\s*:\s*/.test(next)) {
          const keyVal = unquote((/^\s+-\s+key\s*:\s*(.*)$/.exec(next) ?? [])[1] ?? "");
          const varObj: BrandVariable = { key: keyVal, type: "text", label: "", value: "" };
          j++;
          // Read sub-keys until next "  - " or end
          while (j < lines.length) {
            const sub = lines[j] ?? "";
            if (/^\s+-\s+/.test(sub) && !/^\s{4,}-\s+/.test(sub)) break;
            const subMatch = /^\s+(\w+)\s*:\s*(.*)$/.exec(sub);
            if (!subMatch) {
              j++;
              break;
            }
            const subKey = subMatch[1];
            const subVal = unquote(subMatch[2] ?? "");
            if (subKey === "type") {
              varObj.type = subVal as BrandVariableType;
            } else if (subKey === "label") {
              varObj.label = subVal;
            } else if (subKey === "value") {
              varObj.value = subVal;
            } else if (subKey === "section") {
              varObj.section = subVal as BrandSection;
            } else if (subKey === "options") {
              // inline options array
              varObj.options = subVal
                .replace(/^\[|\]$/g, "")
                .split(",")
                .map((s) => unquote(s))
                .filter((s) => s.length > 0);
            } else if (subKey === "fileRef") {
              varObj.fileRef = subVal;
            } else if (subKey === "locked") {
              varObj.locked = subVal === "true";
            } else if (subKey === "enabled") {
              varObj.enabled = subVal === "true";
            }
            j++;
          }
          vars.push(varObj);
        } else if (/^[A-Za-z]/.test(next)) {
          // Back to top-level key
          break;
        } else {
          j++;
        }
      }
      result.variables = vars;
      i = j;
      continue;
    } else {
      i++;
    }
  }

  return result;
}

// ---------------------------------------------------------------------------
// Serializer — emit variables back to YAML
// ---------------------------------------------------------------------------

function serializeVariables(variables: BrandVariable[]): string[] {
  if (variables.length === 0) return ["variables: []"];
  const lines: string[] = ["variables:"];
  for (const v of variables) {
    lines.push(`  - key: ${quoteIfNeeded(v.key)}`);
    lines.push(`    type: ${v.type}`);
    lines.push(`    label: ${quoteIfNeeded(v.label)}`);
    lines.push(`    value: ${quoteIfNeeded(v.value)}`);
    if (v.section) {
      lines.push(`    section: ${v.section}`);
    }
    if (v.options && v.options.length > 0) {
      lines.push(`    options: [${v.options.map(quoteIfNeeded).join(", ")}]`);
    }
    if (v.fileRef) {
      lines.push(`    fileRef: ${quoteIfNeeded(v.fileRef)}`);
    }
    if (v.locked) {
      lines.push(`    locked: true`);
    }
    // Only emit `enabled` when explicitly false — absence is the default
    // (active), so we keep the on-disk file minimal.
    if (v.enabled === false) {
      lines.push(`    enabled: false`);
    }
  }
  return lines;
}

// ---------------------------------------------------------------------------
// Frontmatter rewriter — owned keys only, preserves everything else
// ---------------------------------------------------------------------------

type OwnedKey = "description" | "variables";
const OWNED_KEYS: OwnedKey[] = ["description", "variables"];

interface FrontmatterSpan {
  key: OwnedKey;
  startLine: number;
  endLine: number;
}

function findOwnedSpans(lines: string[]): FrontmatterSpan[] {
  const spans: FrontmatterSpan[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i] ?? "";
    const keyMatch = /^([A-Za-z][A-Za-z0-9_-]*)\s*:\s*(.*)$/.exec(line);
    if (!keyMatch) {
      i++;
      continue;
    }
    const key = keyMatch[1] as OwnedKey;
    const rawValue = keyMatch[2] ?? "";
    if (!OWNED_KEYS.includes(key)) {
      i++;
      continue;
    }
    let end = i + 1;
    if (rawValue.trim() === "" || rawValue.trim() === "[]") {
      // block continues while lines are indented or list items
      while (end < lines.length) {
        const next = lines[end] ?? "";
        if (/^\s+/.test(next)) {
          end++;
        } else {
          break;
        }
      }
    }
    spans.push({ key, startLine: i, endLine: end });
    i = end;
  }
  return spans;
}

function rewriteFrontmatter(originalLines: string[], next: BrandFrontmatter): string[] {
  const spans = findOwnedSpans(originalLines);
  const ownedKeys = new Set(spans.map((s) => s.key));

  const replacements = new Map<OwnedKey, string[]>();

  if (next.description !== undefined) {
    const v = next.description;
    replacements.set("description", v.length > 0 ? [`description: ${quoteIfNeeded(v)}`] : []);
  }
  if (next.variables !== undefined) {
    replacements.set("variables", serializeVariables(next.variables));
  }

  const out: string[] = [];
  let i = 0;
  while (i < originalLines.length) {
    const span = spans.find((s) => s.startLine === i);
    if (span) {
      if (replacements.has(span.key)) {
        out.push(...(replacements.get(span.key) ?? []));
      } else {
        for (let j = span.startLine; j < span.endLine; j++) {
          out.push(originalLines[j] ?? "");
        }
      }
      i = span.endLine;
      continue;
    }
    out.push(originalLines[i] ?? "");
    i++;
  }

  // Append new owned keys not yet in the file
  for (const key of OWNED_KEYS) {
    if (replacements.has(key) && !ownedKeys.has(key)) {
      out.push(...(replacements.get(key) ?? []));
    }
  }

  return out;
}

// ---------------------------------------------------------------------------
// readBrandsFromDir
// ---------------------------------------------------------------------------

async function readBrandsFromDir(scope: BrandScope, dir: string): Promise<BrandEntry[]> {
  let entries: Dirent[];
  try {
    entries = await fs.readdir(dir, { withFileTypes: true });
  } catch {
    return [];
  }
  const results: BrandEntry[] = [];
  for (const entry of entries) {
    if (!entry.isFile()) continue;
    if (!entry.name.endsWith(".md")) continue;
    const name = entry.name.slice(0, -".md".length);
    if (!name || !NAME_REGEX.test(name)) continue;
    const fullPath = path.join(dir, entry.name);
    let stat: { mtime: Date; size: number };
    try {
      const s = await fs.stat(fullPath);
      stat = { mtime: s.mtime, size: s.size };
    } catch {
      continue;
    }
    let parsed: ParsedFrontmatterData;
    let body = "";
    try {
      const text = await fs.readFile(fullPath, "utf8");
      const { rawFrontmatterLines, body: parsedBody, hadFrontmatter } = parseBrandFile(text);
      parsed = hadFrontmatter
        ? parseFrontmatter(rawFrontmatterLines)
        : { description: "", variables: [] };
      body = parsedBody;
    } catch {
      parsed = { description: "", variables: [] };
    }
    results.push({
      id: `${scope}:${name}`,
      scope,
      name,
      description: parsed.description,
      variables: parsed.variables,
      path: fullPath,
      modifiedAt: stat.mtime.toISOString(),
      size: stat.size,
      body,
    });
  }
  results.sort((a, b) => a.name.localeCompare(b.name));
  return results;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export async function listBrands(args: ListBrandsArgs): Promise<BrandEntry[]> {
  // When tokens.json is present, merge token-backed sections with prose/*.md sections.
  // When tokens.json is absent, fall back to scanning all .md files in the brand dir.
  if (args.workspaceRoot) {
    const { tokensJsonExists, listBrandsFromTokensJson } = await import("./tokens-adapter.js");
    if (await tokensJsonExists(args.workspaceRoot)) {
      const tokenEntries = await listBrandsFromTokensJson(args.workspaceRoot, "project");
      const proseDir = path.join(args.workspaceRoot, ".appostle", "brand", "prose");
      const proseEntries = await readBrandsFromDir("project", proseDir);
      // Prose files whose name matches a token-adapter section are already merged
      // into that section's body by listBrandsFromTokensJson — drop duplicates.
      const tokenNames = new Set(tokenEntries.map((e) => e.name));
      const extraProseEntries = proseEntries.filter((e) => !tokenNames.has(e.name));
      return [...tokenEntries, ...extraProseEntries];
    }
  }
  return readBrandsFromDir("project", resolveScopeDir("project", args.workspaceRoot));
}

export async function createBrand(args: CreateBrandArgs): Promise<CreateBrandResult> {
  if (!NAME_REGEX.test(args.name)) {
    throw new Error(
      `Invalid brand name "${args.name}". Use letters, digits, dot, underscore, dash.`,
    );
  }
  if (args.name.includes("/") || args.name.includes("\\") || args.name.includes("..")) {
    throw new Error(`Brand name must not contain path separators or "..".`);
  }

  const dir = resolveScopeDir("project", args.workspaceRoot);
  await fs.mkdir(dir, { recursive: true });

  const filePath = path.join(dir, `${args.name}.md`);
  try {
    await fs.access(filePath);
    throw new Error(`A brand named "${args.name}" already exists at ${filePath}`);
  } catch (err: unknown) {
    if (
      err &&
      typeof err === "object" &&
      "code" in err &&
      (err as { code: string }).code !== "ENOENT"
    ) {
      throw err;
    }
    if (err instanceof Error && err.message.includes("already exists")) {
      throw err;
    }
  }

  await fs.writeFile(filePath, buildStarterBrand(args.name), "utf8");
  return { path: filePath };
}

function buildStarterBrand(name: string): string {
  return `---
name: ${name}
description: ""
variables:
  - key: color.primary
    type: color
    label: Primary Color
    value: "#000000"
  - key: color.accent
    type: color
    label: Accent Color
    value: "#ffffff"
  - key: font.heading
    type: font
    label: Heading Font
    value: ""
  - key: font.body
    type: font
    label: Body Font
    value: ""
  - key: logo
    type: asset
    label: Logo
    value: ""
  - key: tone
    type: text
    label: Tone of Voice
    value: ""
---

# ${name}

Describe this brand's identity, design philosophy, and usage guidelines here.
`;
}

const TARGET_NAME_REGEX = /^[a-z0-9][a-z0-9._-]*$/i;

/**
 * Copies a host file into `<workspaceRoot>/.appostle/brand/assets/` and returns the
 * relative path (relative to `.appostle/brand/`) for storage in `fileRef`.
 *
 * The target name is sanitized; the extension is taken from `sourcePath`.
 * Existing files at the destination are overwritten so a re-pick replaces.
 */
export async function copyBrandAsset(args: CopyBrandAssetArgs): Promise<CopyBrandAssetResult> {
  if (!args.workspaceRoot) {
    throw new Error("workspaceRoot is required to copy a brand asset");
  }
  if (!args.sourcePath || !path.isAbsolute(args.sourcePath)) {
    throw new Error(`copyBrandAsset expects an absolute sourcePath; got "${args.sourcePath}"`);
  }
  if (!TARGET_NAME_REGEX.test(args.targetName) || args.targetName.includes("..")) {
    throw new Error(
      `Invalid targetName "${args.targetName}". Use letters, digits, dot, underscore, dash.`,
    );
  }

  const stats = await fs.stat(args.sourcePath);
  if (!stats.isFile()) {
    throw new Error(`Source path is not a regular file: ${args.sourcePath}`);
  }
  if (stats.size === 0) {
    throw new Error(
      `Source file is empty (0 bytes): ${args.sourcePath}. If this file lives in Dropbox, it may not be downloaded to this machine yet — open it in Finder to trigger the download, then try again.`,
    );
  }

  const ext = path.extname(args.sourcePath).toLowerCase();
  const fileName = `${args.targetName}${ext}`;
  const assetsDir = resolveAssetsDir(args.workspaceRoot, args.category);
  const destAbs = path.resolve(assetsDir, fileName);

  // Defense in depth — make sure dest stays inside the category subfolder.
  const rel = path.relative(path.resolve(assetsDir), destAbs);
  if (rel.startsWith("..") || path.isAbsolute(rel)) {
    throw new Error(`Refusing to write outside of .appostle/brand/assets: ${destAbs}`);
  }

  await fs.mkdir(assetsDir, { recursive: true });
  await removeSiblingExtensions(assetsDir, args.targetName, fileName);
  await fs.copyFile(args.sourcePath, destAbs);

  const derived = await runDerivations({
    primaryAbsolutePath: destAbs,
    assetsDir,
    category: args.category,
    derive: args.derive ?? [],
  });

  return {
    relativePath: relativePathFor(args.category, fileName),
    absolutePath: destAbs,
    derived,
  };
}

/**
 * Uploads raw file bytes into `<workspaceRoot>/.appostle/brand/assets/` and returns the
 * relative path (relative to `.appostle/brand/`) for storage in `fileRef`.
 */
export async function uploadBrandAsset(args: UploadBrandAssetArgs): Promise<CopyBrandAssetResult> {
  if (!args.workspaceRoot) {
    throw new Error("workspaceRoot is required to upload a brand asset");
  }
  if (!TARGET_NAME_REGEX.test(args.targetName) || args.targetName.includes("..")) {
    throw new Error(
      `Invalid targetName "${args.targetName}". Use letters, digits, dot, underscore, dash.`,
    );
  }
  if (!args.dataBase64 || args.dataBase64.trim().length === 0) {
    throw new Error("No file data provided for brand asset upload");
  }

  const extFromSource = args.sourceName ? path.extname(args.sourceName).toLowerCase() : "";
  const ext = extFromSource || ".png";
  const fileName = `${args.targetName}${ext}`;
  const assetsDir = resolveAssetsDir(args.workspaceRoot, args.category);
  const destAbs = path.resolve(assetsDir, fileName);

  const rel = path.relative(path.resolve(assetsDir), destAbs);
  if (rel.startsWith("..") || path.isAbsolute(rel)) {
    throw new Error(`Refusing to write outside of .appostle/brand/assets: ${destAbs}`);
  }

  let data: Buffer;
  try {
    data = Buffer.from(args.dataBase64, "base64");
  } catch {
    throw new Error("Failed to decode uploaded brand asset");
  }
  if (data.length === 0) {
    throw new Error("Uploaded brand asset is empty");
  }

  await fs.mkdir(assetsDir, { recursive: true });
  await removeSiblingExtensions(assetsDir, args.targetName, fileName);
  await fs.writeFile(destAbs, data);

  const derived = await runDerivations({
    primaryAbsolutePath: destAbs,
    assetsDir,
    category: args.category,
    derive: args.derive ?? [],
  });

  return {
    relativePath: relativePathFor(args.category, fileName),
    absolutePath: destAbs,
    derived,
  };
}

// ---------------------------------------------------------------------------
// Brand asset delete / rename
// ---------------------------------------------------------------------------
//
// Used by the dynamic record-list flow on shapes.md (and any future
// record-list consumer): when a record is deleted from the brand file the UI
// asks the daemon to drop the SVG that the record's asset field pointed to;
// when a record's label cascade-renames its slug, the SVG file is renamed on
// disk so the filename matches the new key.

export interface DeleteBrandAssetArgs {
  workspaceRoot: string;
  /** Relative path inside `.appostle/brand/`, e.g. "assets/shape/circular-text.svg". */
  relativePath: string;
}

export interface RenameBrandAssetArgs {
  workspaceRoot: string;
  /** Relative path inside `.appostle/brand/`, e.g. "assets/shape/circular-text.svg". */
  fromRelativePath: string;
  /** Bare stem (no extension) for the new filename. Extension is taken from the source. */
  toTargetName: string;
}

export interface RenameBrandAssetResult {
  /** New relative path inside `.appostle/brand/`. */
  relativePath: string;
  /** New absolute path on disk. */
  absolutePath: string;
}

function resolveAssetAbsolutePath(workspaceRoot: string, relativePath: string): string {
  const brandRoot = path.join(workspaceRoot, ".appostle", "brand");
  const abs = path.resolve(brandRoot, relativePath);
  const rel = path.relative(path.resolve(brandRoot), abs);
  if (rel.startsWith("..") || path.isAbsolute(rel)) {
    throw new Error(`Refusing to operate outside of .appostle/brand: ${relativePath}`);
  }
  if (!rel.startsWith("assets/") && !rel.startsWith("assets\\")) {
    throw new Error(`Brand asset path must live under assets/: ${relativePath}`);
  }
  return abs;
}

/**
 * Delete a single brand asset file. No-ops when the file does not exist (so
 * callers can be idempotent across reorders / undo / repeated saves).
 */
export async function deleteBrandAsset(args: DeleteBrandAssetArgs): Promise<void> {
  if (!args.workspaceRoot) {
    throw new Error("workspaceRoot is required to delete a brand asset");
  }
  if (!args.relativePath || args.relativePath.includes("..")) {
    throw new Error(`Invalid relativePath "${args.relativePath}"`);
  }
  const abs = resolveAssetAbsolutePath(args.workspaceRoot, args.relativePath);
  try {
    const stat = await fs.stat(abs);
    if (!stat.isFile()) return;
    await fs.unlink(abs);
  } catch (err) {
    const code = (err as NodeJS.ErrnoException).code;
    if (code === "ENOENT") return; // already gone — fine
    throw err;
  }
}

/**
 * Rename a single brand asset file in place, preserving its parent directory
 * and extension. Used by the record-list cascade-rename path so the asset
 * filename stays in sync with the record's slug.
 *
 * No-ops (returning the source path) when the source file does not exist —
 * the caller may still be tracking a record whose asset was never uploaded.
 */
export async function renameBrandAsset(
  args: RenameBrandAssetArgs,
): Promise<RenameBrandAssetResult> {
  if (!args.workspaceRoot) {
    throw new Error("workspaceRoot is required to rename a brand asset");
  }
  if (!args.fromRelativePath || args.fromRelativePath.includes("..")) {
    throw new Error(`Invalid fromRelativePath "${args.fromRelativePath}"`);
  }
  if (!TARGET_NAME_REGEX.test(args.toTargetName) || args.toTargetName.includes("..")) {
    throw new Error(
      `Invalid toTargetName "${args.toTargetName}". Use letters, digits, dot, underscore, dash.`,
    );
  }

  const fromAbs = resolveAssetAbsolutePath(args.workspaceRoot, args.fromRelativePath);
  const ext = path.extname(fromAbs);
  const dir = path.dirname(fromAbs);
  const toFileName = `${args.toTargetName}${ext}`;
  const toAbs = path.resolve(dir, toFileName);

  // Same-name no-op (case-sensitive comparison; macOS HFS+/APFS are
  // case-insensitive but fs.rename is the right call regardless).
  if (fromAbs === toAbs) {
    return {
      relativePath: args.fromRelativePath,
      absolutePath: fromAbs,
    };
  }

  // Source may not exist (record had no uploaded asset yet) — surface that as
  // a no-op rather than an error so the cascade-rename in the UI can run
  // unconditionally without per-record existence checks.
  try {
    await fs.access(fromAbs);
  } catch (err) {
    const code = (err as NodeJS.ErrnoException).code;
    if (code === "ENOENT") {
      // Compute the would-be relative path for the caller even though we
      // didn't actually move anything — keeps the UI's optimistic update
      // honest about where the asset will live once it's uploaded.
      const brandRoot = path.join(args.workspaceRoot, ".appostle", "brand");
      const newRel = path.relative(brandRoot, toAbs).split(path.sep).join("/");
      return { relativePath: newRel, absolutePath: toAbs };
    }
    throw err;
  }

  await fs.rename(fromAbs, toAbs);
  const brandRoot = path.join(args.workspaceRoot, ".appostle", "brand");
  const newRel = path.relative(brandRoot, toAbs).split(path.sep).join("/");
  return { relativePath: newRel, absolutePath: toAbs };
}

export async function writeBrandFrontmatter(
  args: WriteBrandFrontmatterArgs,
  workspaceRoot?: string,
): Promise<void> {
  if (!path.isAbsolute(args.path)) {
    throw new Error(`writeBrandFrontmatter expects an absolute path; got "${args.path}"`);
  }
  if (!isInsideAllowedRoot(args.path, workspaceRoot)) {
    throw new Error(`Path "${args.path}" is not inside an allowlisted brand root`);
  }

  let original: string;
  try {
    original = await fs.readFile(args.path, "utf8");
  } catch (err) {
    throw new Error(
      `Failed to read brand file: ${err instanceof Error ? err.message : String(err)}`,
    );
  }

  const parsed = parseBrandFile(original);
  const newLines = rewriteFrontmatter(parsed.rawFrontmatterLines, args.frontmatter);
  const nextFrontmatter = ["---", ...newLines, "---"].join("\n");
  const nextContent = parsed.hadFrontmatter
    ? `${nextFrontmatter}\n${parsed.body}`
    : `${nextFrontmatter}\n\n${original}`;

  await fs.writeFile(args.path, nextContent, "utf8");
}
